import { useState } from 'react';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Badge,
  Button,
  TextArea,
  toast,
} from '@etherisc/ui-kit';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { ledgerService } from '../../services/ledger/ledgerService';
import { PermissionGate } from '../../components/PermissionGate';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { Modal } from '../../components/ledger/Modal';

export function JournalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reversalDialog, setReversalDialog] = useState(false);
  const [reversalReason, setReversalReason] = useState('');
  const [isReversing, setIsReversing] = useState(false);

  const {
    data: journal,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ledger', 'journal', id],
    queryFn: () => ledgerService.getJournalById(id!),
    enabled: !!id,
  });

  const handleReverseJournal = async () => {
    if (!id || !reversalReason.trim()) return;

    setIsReversing(true);
    try {
      await ledgerService.reverseJournal(id, reversalReason.trim());
      toast.success('Journal reversed successfully');
      setReversalDialog(false);
      setReversalReason('');
      refetch(); // Refresh journal data
    } catch (error) {
      toast.error('Failed to reverse journal', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsReversing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">Loading journal...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !journal) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>Error loading journal: {error?.message || 'Journal not found'}</p>
              <Button onClick={() => navigate('/ledger/journals')} className="mt-4">
                Back to Journals
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusVariant = journal.status === 'POSTED' ? 'success' : 
                       journal.status === 'REVERSED' ? 'destructive' : 'secondary';

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/ledger/journals')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Journals
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Journal Detail</h1>
          <p className="text-muted-foreground">
            {journal.externalRef} - {journal.description}
          </p>
        </div>
      </div>

      {/* Journal Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                Journal {journal.externalRef}
                <Badge variant={statusVariant}>{journal.status}</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {journal.description}
              </p>
            </div>
            <PermissionGate permission="ledger:reverse">
              {journal.status === 'POSTED' && (
                <Button
                  variant="outline"
                  onClick={() => setReversalDialog(true)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reverse Journal
                </Button>
              )}
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Journal Date</label>
              <p className="font-mono">{formatDate(journal.journalDate)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Total Amount</label>
              <p className="font-mono">{formatCurrency(parseFloat(journal.totalAmount))}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Posted At</label>
              <p className="font-mono">
                {journal.postedAt ? formatDate(journal.postedAt) : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Reversed At</label>
              <p className="font-mono">
                {journal.reversedAt ? formatDate(journal.reversedAt) : '-'}
              </p>
            </div>
          </div>
          
          {journal.reversalRef && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm">
                <strong>Reversal Reference:</strong> {journal.reversalRef}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Journal Lines */}
      <Card>
        <CardHeader>
          <CardTitle>Journal Lines</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Line #</th>
                  <th className="text-left p-2">Account</th>
                  <th className="text-left p-2">Description</th>
                  <th className="text-right p-2">Debit</th>
                  <th className="text-right p-2">Credit</th>
                </tr>
              </thead>
              <tbody>
                {journal.lines?.map((line: any) => (
                  <tr key={line.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-mono">{line.lineNumber}</td>
                    <td className="p-2">
                      <div>
                        <span className="font-mono text-sm">{line.accountCode}</span>
                        <br />
                        <span className="text-sm text-muted-foreground">{line.accountName}</span>
                      </div>
                    </td>
                    <td className="p-2">{line.description || '-'}</td>
                    <td className="p-2 text-right font-mono">
                      {line.debitAmount !== '0.00' ? formatCurrency(parseFloat(line.debitAmount)) : '-'}
                    </td>
                    <td className="p-2 text-right font-mono">
                      {line.creditAmount !== '0.00' ? formatCurrency(parseFloat(line.creditAmount)) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-semibold">
                  <td colSpan={3} className="p-2">Total</td>
                  <td className="p-2 text-right font-mono">
                    {formatCurrency(parseFloat(journal.totalAmount))}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {formatCurrency(parseFloat(journal.totalAmount))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Reversal Modal */}
      <Modal
        isOpen={reversalDialog}
        onClose={() => {
          setReversalDialog(false);
          setReversalReason('');
        }}
        title="Reverse Journal"
        description={`You are about to reverse journal "${journal.externalRef}". This action cannot be undone. Please provide a reason for the reversal.`}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setReversalDialog(false);
                setReversalReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleReverseJournal}
              disabled={!reversalReason.trim() || isReversing}
            >
              {isReversing ? 'Reversing...' : 'Reverse Journal'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextArea
            label="Reason for Reversal"
            placeholder="e.g., Correction - duplicate entry posted in error"
            value={reversalReason}
            onChange={(e) => setReversalReason(e.target.value)}
            required
          />
        </div>
      </Modal>
    </div>
  );
}
